import { useState } from "react";
import { toast } from "sonner";
import { Download, Eye } from "lucide-react";
import { downloadBlob, api, formatApiError } from "@/lib/api";
import { useMeta } from "@/hooks/useMeta";
import { PageHeader } from "@/components/Doodles";

type Opt = { value: string; label: string };

function Choice({ label, value, options, onChange, testId }: { label: string; value: string; options: Opt[]; onChange: (v: string) => void; testId: string }) {
  return (
    <fieldset className="mb-6">
      <legend className="mb-2 text-sm font-bold text-[#23324A]">{label}</legend>
      <div className="flex flex-wrap gap-2" data-testid={testId}>
        {options.map((o) => (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} data-testid={`${testId}-${o.value}`}
            className={`chip ${value === o.value ? "chip-active" : ""}`}>
            {o.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

const toOpts = (rec: Record<string, any>, key?: string) => Object.entries(rec).map(([value, v]) => ({ value, label: key ? v[key] : v }));

const KID_ONLY = ["her", "him", "kid", "family"];
const allowed = (template: string, r: string) =>
  template === "gift_tags" ? ["family", "teacher"].includes(r)
    : template === "wrapping_paper" || template === "scavenger_hunt" ? r === "family"
    : template === "fill_in_card" ? !KID_ONLY.includes(r)
    : r !== "family";

export default function StudioPage() {
  const meta = useMeta();
  const [template, setTemplate] = useState("fill_in_card");
  const [variant, setVariant] = useState("drawing");
  const [recipient, setRecipient] = useState("grandma");
  const [season, setSeason] = useState("christmas");
  const [paper, setPaper] = useState("letter");
  const [pages, setPages] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  if (!meta) return <p className="text-sm text-[#23324A]/60">Loading…</p>;

  const recipients = toOpts(meta.recipients).filter((r) => allowed(template, r.value));
  const pickTemplate = (t: string) => {
    setTemplate(t);
    if (!allowed(t, recipient)) setRecipient(t === "gift_tags" || t === "wrapping_paper" || t === "scavenger_hunt" ? "family" : t === "reasons_list" ? "her" : "mom");
  };
  const body = { component: { template, recipient, variant }, season, paper };

  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/printables/preview", body);
      setPages(data.pages);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const download = () => downloadBlob("/printables/pdf", `${template}-${recipient}-${paper}.pdf`, body).catch((e) => toast.error(formatApiError(e)));

  return (
    <div data-testid="studio-page">
      <PageHeader title="Template studio" kicker="6 templates · coloring templates live under Coloring" />
      <div className="grid gap-10 lg:grid-cols-[380px_1fr]">
        <div className="paper-card p-6 fade-up">
          <Choice label="Template" value={template} onChange={pickTemplate} options={toOpts(meta.templates)} testId="studio-template" />
          {template === "fill_in_card" && (
            <Choice label="Box" value={variant} onChange={setVariant} testId="studio-variant"
              options={[{ value: "drawing", label: "Drawing box" }, { value: "handprint", label: "Handprint here" }]} />
          )}
          {template !== "wrapping_paper" && template !== "scavenger_hunt" && <Choice label="For" value={recipient} onChange={setRecipient} options={recipients} testId="studio-recipient" />}
          <Choice label="Season colors" value={season} onChange={setSeason} options={toOpts(meta.seasons, "name")} testId="studio-season" />
          <Choice label="Paper" value={paper} onChange={setPaper} options={toOpts(meta.papers)} testId="studio-paper" />
          <div className="flex flex-wrap gap-3">
            <button onClick={preview} disabled={busy} className="btn-coral" data-testid="studio-preview-btn"><Eye className="h-4 w-4" strokeWidth={2.5} />{busy ? "Rendering…" : "Preview"}</button>
            <button onClick={download} className="btn-outline" data-testid="studio-download-btn"><Download className="h-4 w-4" strokeWidth={2.5} />Download PDF</button>
          </div>
        </div>
        <div className="min-w-0">
          {pages.length === 0 ? (
            <div className="flex h-80 items-center justify-center rounded-[24px] border-2 border-dashed border-[#23324A]/30 text-sm font-semibold text-[#23324A]/60" data-testid="studio-empty">
              Pick options and tap Preview. Print one at 100% to test.
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2" data-testid="studio-preview">
              {pages.map((src, i) => (
                <img key={i} src={src} alt={`Page ${i + 1}`} className="w-full rounded-xl border-2 border-[#23324A] bg-white shadow-[5px_5px_0_#23324A] fade-up" data-testid={`studio-page-${i}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
